import React, { useState, useEffect } from 'react';
import axios from 'axios';

const EditNavbar = ({ isOpen, onClose, initialData }) => {
  const [navbarData, setNavbarData] = useState({
    logo: "",
    hotline: ""
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch current data when modal opens
  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/layout/navbar/`);
          if (response.ok) {
            const data = await response.json();
            setNavbarData(data);
          }
        } catch (error) {
          console.error('Error fetching navbar data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  // If initialData is provided, use that instead of fetching
  useEffect(() => {
    if (initialData) {
      setNavbarData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNavbarData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'navbar_logos');

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      // Update the logo with the URL from the response
      setNavbarData(prev => ({
        ...prev,
        logo: response.data.image || response.data.image.url
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/layout/navbar/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(navbarData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        window.location.reload(); // Only reload if needed
      } else {
        console.error('Failed to update navbar data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating navbar data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading navbar data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-6">Edit Navbar</h3>
        
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="card bg-base-100 shadow-sm p-4">
            <h4 className="font-bold mb-4">Logo</h4>
            <div className="form-control">
              <label className="label">
                <span className="label-text">Current Logo</span>
              </label>
              {navbarData.logo && (
                <img 
                  src={navbarData.logo} 
                  alt="Current logo preview" 
                  className="max-h-24 rounded-md border mb-4"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/200x80?text=Logo+Not+Found';
                  }}
                />
              )}
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input file-input-bordered w-full"
                disabled={isUploading}
              />
              {isUploading && (
                <div className="mt-2">
                  <progress 
                    className="progress progress-primary w-full" 
                    value={uploadProgress} 
                    max="100"
                  ></progress>
                  <p className="text-sm text-gray-500 mt-1">Uploading: {uploadProgress}%</p>
                </div>
              )}
            </div>
          </div>

          {/* Hotline Number */}
          <div className="card bg-base-100 shadow-sm p-4">
            <h4 className="font-bold mb-4">Hotline Number</h4>
            <div className="form-control">
              <label className="label">
                <span className="label-text">Phone Number</span>
              </label>
              <input 
                type="tel" 
                name="hotline"
                value={navbarData.hotline || ""} 
                onChange={handleChange} 
                className="input input-bordered w-full" 
                placeholder="+880 1818 773 300"
              />
            </div>
          </div>
        </div>
        
        <div className="modal-action mt-6">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default EditNavbar;